import { UI2IconName } from '../../icons/ui2-icon-names'
import {
  RawSelectOption,
  SelectableFieldProps,
  SelectOption,
} from '../core-field/selectable-field-props'

export type RadioButtonStyle = 'default' | 'pill'
export type RadioOptionLayout = 'horizontal' | 'vertical'
export type PillVariant = 'segmented' | 'standalone'

/**
 * Semantic color family for a standalone pill's selected highlight. The
 * component owns the actual shade used for each family name.
 */
export type RadioOptionColorFamily =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'teal'
  | 'blue'
  | 'purple'
  | 'grey'

/**
 * Resolved shades for one semantic color family. Modelled on the status-badge
 * treatment — a light tint behind darker text — rather than a saturated fill
 * with white text, so a colored option reads as a quiet label rather than a
 * second call to action next to the form's own buttons.
 */
export interface RadioOptionColorTreatment {
  /** Tinted fill behind a selected option. */
  background: string
  /** Label and icon color on top of `background`. */
  foreground: string
  /** Border of a selected option. */
  border: string
}

/**
 * The shades each family resolves to. Literal values rather than per-platform
 * tokens: NM and web must land on the same color, and neither palette carries
 * every family.
 */
export const RADIO_OPTION_COLOR_TREATMENTS: Record<
  RadioOptionColorFamily,
  RadioOptionColorTreatment
> = {
  red: { background: '#ffcdd2', foreground: '#c62828', border: '#e53935' },
  orange: { background: '#ffe0b2', foreground: '#ef6c00', border: '#fb8c00' },
  yellow: { background: '#fff9c4', foreground: '#f57f17', border: '#fdd835' },
  green: { background: '#c8e6c9', foreground: '#2e7d32', border: '#43a047' },
  teal: { background: '#b2dfdb', foreground: '#00695c', border: '#00897b' },
  blue: { background: '#bbdefb', foreground: '#1565c0', border: '#1e88e5' },
  purple: { background: '#e1bee7', foreground: '#6a1b9a', border: '#8e24aa' },
  grey: { background: '#eeeeee', foreground: '#424242', border: '#9e9e9e' },
}

export function getRadioOptionColorTreatment(
  family?: RadioOptionColorFamily,
): RadioOptionColorTreatment | undefined {
  return family ? RADIO_OPTION_COLOR_TREATMENTS[family] : undefined
}

/**
 * Radio-button options carry a few per-option display extras on top of the
 * canonical `{ label, value }` shape.
 */
export interface RadioOption extends SelectOption {
  /** Per-option icon — standalone pill style only. */
  icon?: UI2IconName
  /**
   * Semantic color family applied to the option's selected highlight —
   * standalone pill style only. The component owns the actual shade for each
   * family; do not pass raw CSS color strings.
   */
  selectedColorFamily?: RadioOptionColorFamily
  /**
   * Supporting line rendered beneath this option's label. Ignored in the
   * segmented pill variant, whose equal-width cells have no room for it.
   */
  helpText?: string
  /**
   * Renders this option visibly but non-interactively. Independent of the
   * field's own `isDisabled`, which disables every option.
   */
  isDisabled?: boolean
}

export type RawRadioOption = RawSelectOption | RadioOption

export const UI2_RADIO_BUTTON_FIELD_DEFAULT_PROPS: Partial<UI2RadioButtonFieldProps> = {
  buttonStyle: 'default',
  isMultiSelect: false,
}

export interface UI2RadioButtonFieldProps extends SelectableFieldProps {
  buttonStyle?: RadioButtonStyle
  pillVariant?: PillVariant
  optionLayout?: RadioOptionLayout
  options?: RawRadioOption[]
}
